/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useCallback, useMemo } from 'react'
import { Formik, Form, } from 'formik'
import TextField from '../formik/TextField'
import * as Yup from "yup";
import Typography from '@mui/material/Typography';
import { useDispatch, useSelector } from 'react-redux';
import { IState } from '../../reducers';
import MikeButton from '../mike-button/MikeButton';

import { ICreateMeshInfo, ICreateMeshParameter, IEntity } from '../../reducers/createMesh';
import MikeDialog from '../DialogComponents/MikeDialog';
import MuiDialogContent from '@mui/material/DialogContent';
import { updateParameters } from '../../actions/createMesh';
import { useIntl } from 'react-intl';
import ContextHelp from '../ContextHelp';
import mikeSharedTheme from '../../styles/mikeSharedTheme';

const titleStyle = css`
  padding-bottom:${mikeSharedTheme.spacing(1)};
`
const buttonsContainerStyle = css`
  display: flex;
  justify-content: flex-end;
  padding: ${mikeSharedTheme.spacing(2)}; 
`
 const dialogContent = css`
    background-color: ${mikeSharedTheme.palette.background.paper};    
    paddingBottom: ${mikeSharedTheme.spacing(5)};
  `

  const flexStyle = css`
  display: flex
`

interface IProps {  
  datasetType: string; 
  createMeshDataset: IEntity; 
  onCancel: () => void;
  onOk: () => void;
}
const numericTypes = ["integer", "double"]
const MeshOutlinePropertiesForm = (props: IProps) => { 
  const { datasetType, createMeshDataset, onCancel, onOk } = props;
  const intl = useIntl();
  const dispatch = useDispatch();



  const createMeshPayload: ICreateMeshInfo = useSelector((state: IState) => state.createMesh.createMeshPayload); 

  const parameters: Array<ICreateMeshParameter> = useMemo(() => {
    const params = datasetType && createMeshPayload && createMeshPayload.parameterDescriptions ? createMeshPayload.parameterDescriptions.filter((p: ICreateMeshParameter) => p.entityType.toLowerCase() === datasetType.toLowerCase()) : []   
    return params
  }, [createMeshPayload, datasetType])

  const {initialValues, validationSchema } = useMemo(() => {
    if (parameters && parameters.length > 0){
      const defaultValues =  parameters.map((p: ICreateMeshParameter) => {
        return [p.name, p.value ? p.value : p.defaultValue]
      })    
      const isRequired = "is required"
      const numericValueRequired = "is required as numeric value" 
      const entries =  parameters.map((p: ICreateMeshParameter) => { 
        return [p.name, numericTypes.includes(p.valueType) ?  Yup.number().typeError(numericValueRequired).required(isRequired).min(p.minValue, p.minValue + "-" + p.maxValue).required(p.minValue + "-" + p.maxValue).max(p.maxValue, p.minValue + "-" + p.maxValue).required(p.minValue + "-" + p.maxValue) : Yup.string().required(isRequired)]
      })
      const val = Yup.object().shape(Object.fromEntries(entries))
      return { initialValues: Object.fromEntries(defaultValues), validationSchema: val} //Yup.object().shape({params})} ;
    }
    return { initialValues: undefined, validationSchema: undefined }
  }, [parameters]) 

  const onSubmit = useCallback((values) => {     
    const vals = {...values}
    const keys = Object.keys(values)
    keys.forEach((key: string) => {
      const parameter = parameters.find((p: ICreateMeshParameter) => p.name === key)
      const isNumeric = numericTypes.includes(parameter.valueType)
      vals[key] = isNumeric ? parseInt(values[key]) : values[key]
    })
    dispatch(updateParameters(vals, datasetType, createMeshDataset.id)) 
    onOk();   
  }, [dispatch, onOk, parameters, createMeshDataset, datasetType])

  return (   
    <MikeDialog 
      open={createMeshDataset !== null}
      onCancel={onCancel} 
      hideActions
      dialogTitle={intl.formatMessage({id: 'outlineProperties.parameters'})}    
    >  
      <MuiDialogContent css={dialogContent}>
        <Formik
          validationSchema={validationSchema}
          initialValues={initialValues}
          enableReinitialize     
          onSubmit={onSubmit}
          render={formikProps => {
            const { handleChange, setFieldTouched, handleSubmit, isValid } = formikProps
          
            const handleOnChange = (name, e) => {
              e.persist()
              handleChange(e)
              setFieldTouched(name, true, false)              
            } 

            return (
              <Form>
                <Typography css={titleStyle} variant="body2">{intl.formatMessage({id: 'outlineProperties.adaptDefaultValues'})}</Typography>
                {parameters.map((p: ICreateMeshParameter, index: number) => {
                  return (
                    <div css={flexStyle} key={index}>
                       <TextField                        
                        onChange={handleOnChange.bind(null, 'name')}
                        name={p.name}       
                        label={p.displayName}                                        
                        addPlaceHolder={true}
                      />
                      {p.info &&  <ContextHelp helpTexts={p.info} primary/>}                     
                    </div>                   
                    )
                  })
                }
              
                <div css={buttonsContainerStyle}>
                  <MikeButton disabled={!isValid} id="resolutionButton" variant="text" onClick={() => handleSubmit()}>
                    {intl.formatMessage({id: 'outlineProperties.submitChange'})}               
                  </MikeButton>
                </div>
              </Form>        
            )}}
          />
        </MuiDialogContent>
      </MikeDialog> 
  )
}

export default MeshOutlinePropertiesForm